import React, { useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  CustomerService01Icon, 
  Mail01Icon,
  Message01Icon,
  InformationCircleIcon,
  Shield02Icon,
  LockPasswordIcon,
  CreditCardIcon
} from '@hugeicons/core-free-icons';
import { Button } from "../components/ui/button";
import { useAuth } from '../contexts/AuthContext';
import ResponsiveModal from '../components/ResponsiveModal';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const Support = () => {
  const { user } = useAuth();
  const [isSecurityModalOpen, setIsSecurityModalOpen] = useState(true);

  const generateTicketId = () => {
    return Math.random().toString(36).substring(2, 6).toUpperCase();
  };

  const ticketId = generateTicketId();
  const userName = user?.name || user?.business_name || 'Valued Client';
  const mailSubject = encodeURIComponent(`Enquiry about [WHAT]? Ticket ${ticketId}`);
  const mailBody = encodeURIComponent(`Hey Support, I'm ${userName} and I'm experiencing [WHAT].\n\nHere's my ticket ID: ${ticketId}\n\nOther details: [Add if any]`);
  const mailtoUrl = `mailto:support@safzi.com?subject=${mailSubject}&body=${mailBody}`;

  const faqItems = [
    { 
      title: "Verification Issues", 
      icon: Shield02Icon, 
      color: "text-emerald-500",
      answer: "If your application was rejected, it typically means your linked account is not registered as an authorized Payment Agent on Deriv. Safzi requires a valid PA status to automatically grant the Agent role. Please verify your status on Deriv before reapplying."
    },
    { 
      title: "Payment Not Reflecting", 
      icon: CreditCardIcon, 
      color: "text-blue-500",
      answer: "Try manually refreshing your balance widget or the entire page. If the issue persists, please check your transaction status directly on Deriv. If Deriv confirms completion but Safzi doesn't reflect it, contact your agent locally to manually sync the state."
    },
    { 
      title: "Deriv Token Errors", 
      icon: LockPasswordIcon, 
      color: "text-purple-500",
      answer: "Ensure your API token has both 'Admin' and 'Payments' scopes enabled in your Deriv settings. If you receive a 'Session Expired' error, try unlinking and re-linking your account to generate a fresh connection."
    },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-12 pb-24 px-4 sm:px-6 antialiased">
      {/* Hero Header */}
      <div className="text-center space-y-4 pt-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/5 border border-primary/10 text-primary text-[10px] font-black uppercase tracking-[0.2em] mb-2 shadow-sm">
          <HugeiconsIcon icon={InformationCircleIcon} size={12} />
          Help Center
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground">How can we <span className="italic font-serif">help you?</span></h1>
        <p className="text-muted-foreground font-medium text-lg max-w-xl mx-auto">
          Our specialized support team is available around the clock to ensure your trades are seamless.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Live Instant Support Card */}
        <Card className="border-emerald-500/20 shadow-sm transition-all hover:shadow-xl dark:bg-zinc-950/50 flex flex-col group overflow-hidden relative">
          <div className="absolute top-0 right-0 p-8 opacity-[0.03] transform group-hover:scale-110 transition-transform duration-700 pointer-events-none">
            <HugeiconsIcon icon={CustomerService01Icon} size={180} />
          </div>
          <CardHeader className="relative z-10">
            <div className="bg-emerald-500/10 w-14 h-14 rounded-2xl flex items-center justify-center text-emerald-500 border border-emerald-500/20 mb-4 shadow-inner">
              <HugeiconsIcon icon={CustomerService01Icon} size={28} />
            </div>
            <CardTitle className="text-2xl font-bold">Instant Live Chat</CardTitle>
            <CardDescription className="text-base font-medium">Real-time resolution for urgent trade issues.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-8 mt-auto relative z-10">
            <div className="flex items-center justify-between p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
              <div className="space-y-0.5">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Typical Wait Time</p>
                <p className="text-lg font-bold text-emerald-600">{"<"} 2 Minutes</p>
              </div>
              <div className="flex gap-1">
                {[1, 2, 3].map(i => <div key={i} className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" style={{ animationDelay: `${i * 0.2}s` }} />)}
              </div>
            </div>
            <Button asChild className="w-full h-14 text-base font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-900/20">
              <a href="https://tawk.to/chat/6987192f9501661c38bbca67/1jgrrkorv" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3">
                <HugeiconsIcon icon={Message01Icon} size={20} />
                Connect with Support
              </a>
            </Button>
          </CardContent>
        </Card>

        {/* Email Support Card */}
        <Card className="border-border/50 shadow-sm transition-all hover:shadow-xl dark:bg-zinc-950/50 flex flex-col group overflow-hidden relative">
          <div className="absolute top-0 right-0 p-8 opacity-[0.03] transform group-hover:scale-110 transition-transform duration-700 pointer-events-none">
            <HugeiconsIcon icon={Mail01Icon} size={180} />
          </div>
          <CardHeader className="relative z-10">
            <div className="bg-primary/5 w-14 h-14 rounded-2xl flex items-center justify-center text-primary border border-border mb-4 shadow-inner">
              <HugeiconsIcon icon={Mail01Icon} size={28} />
            </div>
            <CardTitle className="text-2xl font-bold">Email Enquiry</CardTitle>
            <CardDescription className="text-base font-medium">Detailed assistance for complex account queries.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-8 mt-auto relative z-10">
            <div className="p-4 rounded-xl bg-muted/30 border border-border/50">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">Official Address</p>
              <p className="text-lg font-bold">support@safzi.com</p>
            </div>
            <Button asChild variant="outline" className="w-full h-14 text-base font-bold border-border hover:bg-accent/5 transition-all">
              <a href={mailtoUrl} className="flex items-center justify-center gap-3">
                <HugeiconsIcon icon={Mail01Icon} size={20} />
                Compose Ticket
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Common Queries Grid with Accordion */}
      <div className="space-y-6 pt-4">
        <div className="flex items-center gap-4 px-2">
          <h2 className="text-xs font-black uppercase tracking-[0.3em] text-muted-foreground">Common Inquiries</h2>
          <div className="h-[1px] flex-1 bg-border/50" />
        </div>
        
        <div className="grid grid-cols-1 gap-4">
          <Accordion type="single" collapsible className="w-full space-y-3">
            {faqItems.map((faq, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`} className="border border-border/50 rounded-xl px-2 bg-muted/5">
                <AccordionTrigger className="hover:no-underline py-4 px-4" data-item-value={`item-${idx}`}>
                  <div className="flex items-center gap-4 text-left">
                    <div className={`p-2 rounded-lg bg-background border border-border/50 ${faq.color}`}>
                      <HugeiconsIcon icon={faq.icon} size={18} />
                    </div>
                    <span className="font-bold text-sm sm:text-base">{faq.title}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-6 text-sm text-muted-foreground leading-relaxed font-medium" data-item-value={`item-${idx}`}>
                  <div className="pl-12 border-l-2 border-primary/10">
                    {faq.answer}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>

      {/* Entry Security Modal */}
      <ResponsiveModal 
        isOpen={isSecurityModalOpen} 
        onClose={() => setIsSecurityModalOpen(false)} 
        title="Security Protocol"
      >
        <div className="py-6 text-center space-y-8">
          <div className="bg-rose-500/10 p-6 rounded-full inline-block text-rose-500 border border-rose-500/20 shadow-lg animate-pulse">
            <HugeiconsIcon icon={Shield02Icon} size={64} />
          </div>
          
          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-rose-500 tracking-tight">Security Warning</h2>
            <p className="text-sm text-muted-foreground leading-relaxed px-4 font-medium">
              Safzi engineers or support personnel will <b>never</b> ask for your Deriv password, private API tokens, or payment verification OTPs. 
              <br /><br />
              Protect your credentials and only communicate via these official channels.
            </p>
          </div>

          <div className="px-4 pt-2">
            <Button 
              onClick={() => setIsSecurityModalOpen(false)} 
              className="w-full h-14 bg-rose-600 hover:bg-rose-700 text-white font-bold shadow-lg shadow-rose-900/20"
            >
              I Understand the Risks
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default Support;
